import { useEffect, useState, useMemo, useRef } from 'react';
import BScroll from '@better-scroll/core';
import { createDebugToggleHandler, getDeepValue } from '../../../../utils/index';
import Image from '../../../../components/common/image/image';
import './pano-map.less';

interface IProps {
  moduleConfig: any;
  sceneInfo: any;
  houseInfo: any;
  currentFloorIndex: number;
  krpanoCurRotate: number;
  setKrpanoCurScene: (string) => void;
}

const PanoMap: React.FC<IProps> = (props) => {
  const { moduleConfig, sceneInfo, houseInfo, currentFloorIndex, krpanoCurRotate, setKrpanoCurScene } = props;

  const [openMap, setOpenMap] = useState<boolean>(true);

  const mapScroll = useRef<boolean>(false);
  const radoMapRef = useRef<HTMLDivElement>(null);
  const mapContentRef = useRef<HTMLDivElement | null>(null);
  const bsRef = useRef<any>(null);

  const mapUrl = useMemo(
    () => getDeepValue(houseInfo, `floors.${currentFloorIndex}.map`),
    [houseInfo, currentFloorIndex],
  );

  const extInfoArr = useMemo(() => {
    if (!houseInfo.remark) {
      return [];
    }
    return houseInfo.remark.split('\n');
  }, [houseInfo]);

  const extraPoints =
    useMemo(() => getDeepValue(houseInfo, `floors.${currentFloorIndex}.extraPoints`), [houseInfo]) || [];

  const mapRados = useMemo(() => {
    const curHouseScenes = getDeepValue(houseInfo, 'scenes') || [];
    const sceneMap = getDeepValue(moduleConfig, 'scenes') || [];
    const rados: any[] = [];
    sceneMap.forEach((scene) => {
      if (curHouseScenes.includes(scene?.name)) {
        const { mapRadoPositionX, mapRadoPositionY, offsetAngle, title, id } = scene;
        rados.push({ mapRadoPositionX, mapRadoPositionY, offsetAngle, title, id });
      }
    });
    return [...rados, ...extraPoints];
  }, [houseInfo]);

  const handleArrowBtn = () => {
    setOpenMap(!openMap);
  };

  const initMapContent = () => {
    if (!mapContentRef.current) return;
    mapContentRef.current.style.width = '100%';
    mapContentRef.current.style.height = '100%';
    mapContentRef.current.style.transform = 'none';
  };

  const initMap = () => {
    const mapSize = getDeepValue(houseInfo, `floors.${currentFloorIndex}.mapSize`) || {};
    let scrollX = false;
    let scrollY = false;
    if (mapSize.width && mapSize.width > 350) {
      if (mapScroll.current) {
        initMapContent();
      }
      mapScroll.current = true;
      scrollX = true;
      if (mapContentRef.current) {
        mapContentRef.current.style.width = `${(350 / mapSize.height) * mapSize.width}px`;
      }
    } else if (mapSize.height && mapSize.height > 350) {
      if (mapScroll.current) {
        initMapContent();
      }
      mapScroll.current = true;
      scrollY = true;
      if (mapContentRef.current) {
        mapContentRef.current.style.height = `${(350 / mapSize.width) * mapSize.height}px`;
      }
    } else {
      mapScroll.current = false;
      initMapContent();
    }
    if (mapScroll.current) {
      bsRef.current = new BScroll('.wrapper', {
        scrollY,
        scrollX,
      });
    } else {
      if (bsRef.current) {
        bsRef.current.enabled = false;
      }
    }
  };

  const radoPointPosition = (mapRado) => {
    const { mapRadoPositionX, mapRadoPositionY } = mapRado;
    return {
      top: mapRadoPositionY || 0,
      left: mapRadoPositionX || 0,
    };
  };
  useEffect(() => {
    if (!radoMapRef.current) return;
    let offsetAngle = 0;
    mapRados.forEach((mapRado) => {
      if (mapRado?.id === sceneInfo?.id) {
        offsetAngle = mapRado.offsetAngle;
      }
    });
    radoMapRef.current.style.transform = `rotate(${krpanoCurRotate + offsetAngle}deg)`;
  }, [krpanoCurRotate]);

  const clickHotSpot = (sceneId) => {
    // 更换场景
    const sceneMap = getDeepValue(moduleConfig, 'scenes') || [];
    let sceneName = '';
    sceneMap.forEach((scene) => {
      if (scene?.id === sceneId) {
        sceneName = scene.name;
      }
    });
    if (sceneName && sceneName !== sceneInfo?.name) {
      setKrpanoCurScene(sceneName);
    }
  };

  const headerClick = createDebugToggleHandler();

  useEffect(() => {
    initMap();
  }, [houseInfo]);

  return (
    <div className={`xsdj-roaming-map ${openMap ? '' : 'map-hide'}`}>
      <div className='header' onClick={headerClick}>
        {houseInfo.name && <span className='header-title mr20'>{houseInfo?.name}</span>}
        {sceneInfo.title && <span className='header-title'>{sceneInfo?.title}</span>}
        <div className={`arrow-btn ${openMap ? '' : 'arrow-hide'}`} onClick={handleArrowBtn}>
          <Image id='arrow' className='icon' />
        </div>
      </div>
      <div className={`content ${openMap ? '' : 'content-hide'}`}>
        <div className={`content-map wrapper ${mapScroll.current ? 'content-map-scroll' : ''}`}>
          <div className='content-map-img content' ref={mapContentRef}>
            {mapUrl && <Image className='map-img' src={mapUrl} />}
            {mapRados.map((mapRado, index) => (
              <div className='map-rado' style={radoPointPosition(mapRado)} key={index}>
                {mapRado?.id === sceneInfo?.id ? (
                  <div className='map-rado-content' ref={radoMapRef}>
                    <Image id='map-rado' className='map-rado-img' />
                  </div>
                ) : (
                  <span
                    className='point'
                    onClick={() => clickHotSpot(mapRado.linkedSceneId ? mapRado.linkedSceneId : mapRado.id)}
                  />
                )}
                <div className='map-rado-title'>{mapRado.title || mapRado.name || ''}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={`footer ${openMap ? '' : 'footer-hide'}`}>
        <div className='ext-info'>
          {extInfoArr.map((item) => (
            <div key={item}>{item}</div>
          ))}
        </div>
        {houseInfo.compassOffsetEnabled && (
          <Image id='compass' className='compass' style={{ transform: `rotate(${houseInfo.compassOffsetAngle}deg)` }} />
        )}
      </div>
    </div>
  );
};

export default PanoMap;
